import React, {
    ComponentPropsWithRef,
    ElementType,
    ForwardedRef,
    forwardRef
} from 'react';
import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';
import { MustIncludeOneOfBasicTestIdWithIdProps } from '../common/internal/test-id.types';
import { RequireAtLeastOne } from 'type-fest';
import { focusState } from '../../styles/mixins';
import { useHBUI } from '../HBUIProvider/HBUIProvider';

type WithHref = {
    /** Renders as an anchor element; mutually exclusive with `tag` and `to`. Ctrl/Cmd+click follows the href natively. */
    href?: string;
    tag?: never;
    to?: never;
    LinkComponent?: never;
};

type WithTag = {
    href?: never;
    /** Renders as a custom element type via styled-components `as` prop; mutually exclusive with `href` and `to`. */
    tag?: ElementType;
    to?: never;
    LinkComponent?: never;
};

type WithLink = {
    href?: never;
    tag?: never;
    /** Route path for client-side navigation; renders via HBUIProvider's LinkComponent. Mutually exclusive with `href` and `tag`. Falls back to a native anchor (`<a href={to}>`) with a dev-only console warning when no LinkComponent resolves from HBUIProvider or the `LinkComponent` prop. */
    to?: string;
    /** Overrides HBUIProvider's LinkComponent for this instance. Rarely needed — most consumers should rely on HBUIProvider. */
    LinkComponent?: ElementType;
};

type HrefXorTag = WithHref | WithTag | WithLink;

export type HBClickableProps = ComponentPropsWithRef<typeof motion.div> & {
    /** Disables click/keyboard handlers and sets `aria-disabled`; does not apply native disabled attribute since this renders as a div. */
    isDisabled?: boolean;
    testId?: string;
    /** Called when the element is clicked or activated via Enter/Space. Required unless `onMouseDown` is provided. */
    onClick?: (e) => void;
    /** Called on mousedown. Required unless `onClick` is provided. */
    onMouseDown?: (e) => void;
    /** Enables the focus outline ring on keyboard focus; defaults to true. */
    withFocusState?: boolean;
    /** When true, renders the focus outline inside the element boundary instead of outside. */
    insetFocusState?: boolean;
} & HrefXorTag;

export type Props = RequireAtLeastOne<
    HBClickableProps,
    'onClick' | 'onMouseDown'
>;

const shouldFollowHref = (
    href: string | undefined,
    e: { metaKey: boolean; ctrlKey: boolean }
) => href && (e.metaKey || e.ctrlKey);

const HBClickable: any = forwardRef(
    (
        {
            tabIndex = 0,
            role = 'button',
            onClick,
            children,
            id,
            testIdName,
            testId,
            isDisabled,
            withFocusState = true,
            insetFocusState = false,
            tag,
            href,
            to,
            LinkComponent: linkComponentOverride,
            ...props
        }: Props & MustIncludeOneOfBasicTestIdWithIdProps,
        ref?: ForwardedRef<HTMLDivElement>
    ) => {
        const { LinkComponent: contextLinkComponent } = useHBUI();
        const linkComponent = linkComponentOverride ?? contextLinkComponent;
        const hasLinkComponent = !!(to && linkComponent);
        const fallsBackToAnchor = !!(to && !linkComponent);
        const resolvedHref = href ?? (fallsBackToAnchor ? to : undefined);

        if (process.env.NODE_ENV !== 'production' && fallsBackToAnchor) {
            // eslint-disable-next-line no-console
            console.warn(
                'HBClickable: `to` was provided without a resolvable `LinkComponent` (from HBUIProvider or the `LinkComponent` prop). Falling back to a native anchor — client-side navigation will not occur.'
            );
        }

        const handleClick = e => {
            if (shouldFollowHref(resolvedHref, e)) {
                return;
            }
            if (resolvedHref && onClick) {
                e.preventDefault();
            }
            onClick?.(e);
        };

        const clickableProps: Partial<Props> = {
            ...props,
            tabIndex,
            role,
            onClick: handleClick,
            onMouseDown: props.onMouseDown,
            onKeyPress: e => {
                if (e.key === 'Enter' || e.key === ' ') {
                    handleClick(e);
                }
            }
        };

        const _props = isClickable({ $isDisabled: isDisabled, onClick })
            ? clickableProps
            : props;

        const finalTestId = useBuildTestIdInternal({
            testId,
            identifier: testIdName || id,
            suffix: 'item',
            componentName: 'HBClickable'
        });

        return (
            <Container
                {...(_props as Props)}
                ref={ref}
                data-testid={finalTestId}
                aria-disabled={isDisabled}
                $isDisabled={isDisabled}
                $withFocusState={withFocusState}
                $insetFocusState={insetFocusState}
                id={id}
                as={hasLinkComponent ? linkComponent : resolvedHref ? 'a' : tag}
                href={resolvedHref}
                to={hasLinkComponent ? to : undefined}
            >
                {children}
            </Container>
        );
    }
);

export default HBClickable;

interface IsClickableProps {
    $isDisabled: boolean;
    onClick?: Props['onClick'];
}

const isClickable = ({ $isDisabled, onClick }: IsClickableProps) =>
    !$isDisabled && !!onClick;

export const HB_CLICKABLE_OUTLINE_WIDTH = 5;

interface ContainerProps extends IsClickableProps {
    $withFocusState: boolean;
    $insetFocusState: boolean;
    href?: string;
    to?: string;
}

const Container = styled(motion.div)<ContainerProps>`
    ${props =>
        isClickable(props) &&
        css`
            cursor: ${props.$isDisabled ? 'auto' : 'pointer'};
            ${props.$withFocusState &&
            focusState({ inset: props.$insetFocusState })}
        `}

    ${props =>
        (props.href || props.to) &&
        css`
            text-decoration: none;
            outline: none;
            color: inherit;

            &:hover,
            &:focus,
            &:active {
                color: inherit;
            }
        `}
`;
